import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
} from 'recharts';
import { TrendingUp, Brain, Heart, Calendar, AlertCircle, PenSquare, Sparkles } from 'lucide-react';
import { Insight, JournalEntry as JournalEntryType, SentimentTrend } from '../types';
import { getDateFromDateKey, getEntryDateKey, getLocalDateKey } from '../utils/dateKeys';

interface DashboardProps {
  entries: JournalEntryType[];
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  onStartWriting?: () => void;
}

type InsightSummary = Insight & {
  entry_count: number;
  timeframe_label: string;
};

const getVisibleEmotion = (entry: JournalEntryType) =>
  (entry.emotion && entry.emotion.toLowerCase() !== 'neutral'
    ? entry.emotion
    : entry.emotions_detected?.[0]?.[0]) || 'neutral';

const getMostCommon = (values: string[]) => {
  if (values.length === 0) {
    return 'neutral';
  }

  const counts = values.reduce<Record<string, number>>((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
};

const getMoodInterpretation = (value: number) => {
  if (value >= 8.5) {
    return 'You have mostly been meeting yourself from a strong, steady place.';
  }
  if (value >= 7) {
    return 'You have been carrying things fairly well, with more ease than heaviness.';
  }
  if (value >= 5) {
    return 'Your days look mixed right now, with both steadiness and strain showing up.';
  }
  if (value >= 3.5) {
    return 'The tone has been heavier lately, even when moments of relief still break through.';
  }
  return 'A lot has been weighing on you lately, and that deserves gentleness.';
};

const getStressInterpretation = (value: number) => {
  if (value >= 8) {
    return 'Stress has been arriving loudly and often.';
  }
  if (value >= 6) {
    return 'Stress has been showing up enough to shape the week.';
  }
  if (value >= 3.5) {
    return 'Stress is present, but not defining everything.';
  }
  return 'Things have felt comparatively lighter than your heavier stretches.';
};

type TrendComparison = {
  label: string;
  tone: 'positive' | 'negative' | 'neutral';
};

type ChartTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ value?: number | string }>;
  metricLabel: string;
};

const getEntryTimestamp = (entry: JournalEntryType) => {
  const rawDate = entry.created_at || entry.date || entry.updated_at;
  const parsedDate = new Date(rawDate);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
};

const getAverage = (values: number[]) =>
  values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

const getTrendComparison = (
  entries: JournalEntryType[],
  metric: 'sentiment_score' | 'stress_level',
): TrendComparison => {
  const now = new Date();
  const currentPeriodStart = new Date(now);
  currentPeriodStart.setDate(now.getDate() - 30);
  const previousPeriodStart = new Date(now);
  previousPeriodStart.setDate(now.getDate() - 60);

  const currentValues: number[] = [];
  const previousValues: number[] = [];

  entries.forEach((entry) => {
    const timestamp = getEntryTimestamp(entry);
    const value = entry[metric];

    if (timestamp === null || value === null) {
      return;
    }

    if (timestamp >= currentPeriodStart.getTime() && timestamp <= now.getTime()) {
      currentValues.push(value);
    } else if (
      timestamp >= previousPeriodStart.getTime() &&
      timestamp < currentPeriodStart.getTime()
    ) {
      previousValues.push(value);
    }
  });

  const currentAverage = getAverage(currentValues);
  const previousAverage = getAverage(previousValues);

  if (currentAverage === null || previousAverage === null) {
    return {
      label: 'Not enough data yet',
      tone: 'neutral',
    };
  }

  const delta = currentAverage - previousAverage;
  const isStress = metric === 'stress_level';
  const isPositiveMovement = isStress ? delta < 0 : delta > 0;
  const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
  const signedDelta = `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}`;

  return {
    label: `${arrow} ${signedDelta} vs previous 30 days`,
    tone: Math.abs(delta) < 0.05 ? 'neutral' : isPositiveMovement ? 'positive' : 'negative',
  };
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, label, payload, metricLabel }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const value = payload[0]?.value;
  const numericValue = typeof value === 'number' ? value : Number(value);
  const formattedValue = Number.isFinite(numericValue) ? numericValue.toFixed(1) : 'N/A';
  const formattedDate =
    typeof label === 'string'
      ? getDateFromDateKey(label).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : 'Unknown date';

  return (
    <div className="chart-tooltip">
      <span>{formattedDate}</span>
      <strong>
        {metricLabel}: {formattedValue}/10
      </strong>
    </div>
  );
};

const Dashboard: React.FC<DashboardProps> = ({
  entries,
  loading = false,
  error = '',
  onRetry,
  onStartWriting,
}) => {
  const trends = useMemo<SentimentTrend[]>(() => {
    const dailyData = new Map<
      string,
      {
        sentimentScores: number[];
        stressLevels: number[];
        emotions: string[];
        entryCount: number;
      }
    >();

    entries.forEach((entry) => {
      const dateKey = getEntryDateKey(entry);
      const existing = dailyData.get(dateKey) ?? {
        sentimentScores: [],
        stressLevels: [],
        emotions: [],
        entryCount: 0,
      };

      existing.sentimentScores.push(entry.sentiment_score ?? 0);
      existing.stressLevels.push(entry.stress_level ?? 0);
      existing.emotions.push(getVisibleEmotion(entry));
      existing.entryCount += 1;

      dailyData.set(dateKey, existing);
    });

    return Array.from(dailyData.entries())
      .map(([date, data]) => ({
        date,
        avg_sentiment:
          data.sentimentScores.reduce((sum, score) => sum + score, 0) / data.sentimentScores.length,
        avg_stress:
          data.stressLevels.reduce((sum, score) => sum + score, 0) / data.stressLevels.length,
        most_common_emotion: getMostCommon(data.emotions),
        entry_count: data.entryCount,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [entries]);

  const insights = useMemo<InsightSummary | null>(() => {
    if (entries.length === 0) {
      return null;
    }

    const sentiments = entries.map((entry) => entry.sentiment_score ?? 0);
    const stressLevels = entries.map((entry) => entry.stress_level ?? 0);
    const emotions = entries.map(getVisibleEmotion);
    const datedEntries = entries
      .map((entry) => new Date(entry.date || entry.created_at || entry.updated_at))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    const avgSentiment = sentiments.reduce((sum, score) => sum + score, 0) / sentiments.length;
    const avgStress = stressLevels.reduce((sum, score) => sum + score, 0) / stressLevels.length;
    const mostCommonEmotion = getMostCommon(emotions);

    const uniqueDays = new Set(entries.map(getEntryDateKey)).size;
    const firstEntryDate = datedEntries[0];
    const lastEntryDate = datedEntries[datedEntries.length - 1];
    const spanDays =
      firstEntryDate && lastEntryDate
        ? Math.max(
            1,
            Math.round(
              (lastEntryDate.getTime() - firstEntryDate.getTime()) / (1000 * 60 * 60 * 24),
            ) + 1,
          )
        : uniqueDays;
    const consistencyRatio = spanDays > 0 ? uniqueDays / spanDays : 0;

    const insightLines = [
      `Built from all ${entries.length} saved entries in your journal history.`,
      `Your average mood sits at ${avgSentiment.toFixed(1)}/10, with ${mostCommonEmotion} showing up most often.`,
      `Your average stress level is ${avgStress.toFixed(1)}/10 across ${uniqueDays} writing day${uniqueDays === 1 ? '' : 's'}.`,
    ];

    if (consistencyRatio >= 0.7) {
      insightLines.push(
        `You have written on ${uniqueDays} of the last ${spanDays} tracked days, which shows strong consistency.`,
      );
    } else if (consistencyRatio >= 0.35) {
      insightLines.push(
        `Your writing rhythm is steady enough to show patterns, with entries spread across ${uniqueDays} different days.`,
      );
    } else {
      insightLines.push(
        `Your entries are more occasional right now, so each new reflection will sharpen the pattern tracking.`,
      );
    }

    const suggestions: string[] = [];

    if (avgStress >= 7) {
      suggestions.push(
        'High-stress entries are clustering. A short daily reset or wind-down routine could help.',
      );
    } else if (avgStress >= 4.5) {
      suggestions.push(
        'Stress looks moderate overall. It may help to notice what tends to happen before heavier days.',
      );
    }

    if (avgSentiment <= 3.5) {
      suggestions.push(
        'Your lower-mood entries are prominent. Consider checking in with someone you trust if that pattern continues.',
      );
    } else if (avgSentiment >= 7) {
      suggestions.push(
        'Your overall tone is strong. Revisiting what supports those better days could be useful.',
      );
    }

    if (suggestions.length === 0) {
      suggestions.push(
        'Keep writing with detail. More entries will make the dashboard trends even more useful.',
      );
    }

    return {
      insights: insightLines,
      suggestions,
      data_available: true,
      avg_sentiment: Number(avgSentiment.toFixed(3)),
      avg_stress: Number(avgStress.toFixed(3)),
      entry_count: entries.length,
      timeframe_label: 'all saved entries',
    };
  }, [entries]);

  const formatDate = (dateString: string) =>
    getDateFromDateKey(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const hasWrittenToday = useMemo(() => {
    const today = getLocalDateKey();
    return entries.some((entry) => getEntryDateKey(entry) === today);
  }, [entries]);

  const averageMoodValue =
    entries.length > 0
      ? entries.reduce((sum, entry) => sum + (entry.sentiment_score ?? 0), 0) / entries.length
      : 0;
  const averageStressValue =
    entries.length > 0
      ? entries.reduce((sum, entry) => sum + (entry.stress_level ?? 0), 0) / entries.length
      : 0;
  const averageMood = averageMoodValue.toFixed(1);
  const averageStress = averageStressValue.toFixed(1);
  const moodComparison = useMemo(() => getTrendComparison(entries, 'sentiment_score'), [entries]);
  const stressComparison = useMemo(() => getTrendComparison(entries, 'stress_level'), [entries]);

  if (loading) {
    return (
      <div className="panel-card chart-card">
        <div className="flex items-center justify-center h-64">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: 'transparent', borderBottomColor: 'var(--icon-accent)' }}
          ></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="soft-empty">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-semibold">We could not load your dashboard</h3>
        <p>{error}</p>
        {onRetry ? (
          <button type="button" className="soft-empty-action" onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (trends.length === 0 || !insights) {
    return (
      <div className="soft-empty">
        <div className="soft-empty-illustration" aria-hidden>
          <Calendar className="h-5 w-5" />
          <TrendingUp className="h-5 w-5" />
          <Brain className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-semibold">Your first entry unlocks your dashboard</h3>
        <p>Once you write, Eunoia can begin turning your reflections into gentle patterns.</p>
        {onStartWriting ? (
          <button type="button" className="soft-empty-action" onClick={onStartWriting}>
            <PenSquare className="h-4 w-4" />
            Write your first entry
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="panel-card dashboard-nudge-card">
        <div className="dashboard-nudge-copy">
          <div className="eyebrow">
            <Sparkles className="h-4 w-4" />
            Reflection loop
          </div>
          <h3 className="dashboard-nudge-title">
            {hasWrittenToday
              ? 'You wrote today. Here is how the week is shaping up.'
              : 'You have not written today yet.'}
          </h3>
          <p className="muted-copy">
            {hasWrittenToday
              ? 'Your newest entry is already helping the patterns feel more honest and current.'
              : 'A short check-in now will make the dashboard feel more useful, and more true to where you are.'}
          </p>
        </div>
        {!hasWrittenToday ? (
          <button type="button" className="dashboard-nudge-button" onClick={onStartWriting}>
            <PenSquare className="h-4 w-4" />
            Start your reflection
          </button>
        ) : null}
      </div>

      <div className="overview-grid">
        <div className="metric-card">
          <TrendingUp className="h-5 w-5" style={{ color: 'var(--icon-accent)' }} />
          <strong>{averageMood}</strong>
          <span className={`metric-trend metric-trend-${moodComparison.tone}`}>
            {moodComparison.label}
          </span>
          <span>Mood across all saved entries.</span>
          <p className="metric-meaning">{getMoodInterpretation(averageMoodValue)}</p>
        </div>
        <div className="metric-card">
          <Heart className="h-5 w-5" style={{ color: 'var(--icon-heart)' }} />
          <strong>{averageStress}</strong>
          <span className={`metric-trend metric-trend-${stressComparison.tone}`}>
            {stressComparison.label}
          </span>
          <span>Stress across the same history.</span>
          <p className="metric-meaning">{getStressInterpretation(averageStressValue)}</p>
        </div>
      </div>

      <div className="panel-card insight-card">
        <div className="section-heading">
          <div>
            <div className="eyebrow">
              <Brain className="h-4 w-4" />
              Insight summary
            </div>
            <h2 className="section-title mt-3">A gentle read on everything in your Entries list</h2>
            <p className="muted-copy mt-2">
              This dashboard now summarizes {insights.timeframe_label}, not just your most recent
              few days.
            </p>
          </div>
        </div>

        <div className="insight-list">
          {insights.insights.map((insight, index) => (
            <div key={index} className="insight-pill">
              <span className="insight-pill-icon" aria-hidden>
                {index === 0 ? (
                  <Calendar className="h-4 w-4" />
                ) : index === 1 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : index === 2 ? (
                  <Heart className="h-4 w-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </span>
              {insight}
            </div>
          ))}
        </div>

        {insights.suggestions.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium mb-2" style={{ color: 'var(--heading-strong)' }}>
              Suggested next steps
            </h4>
            <ul className="suggestion-list">
              {insights.suggestions.map((suggestion, index) => (
                <li key={index} className="suggestion-item">
                  <span className="mt-1" style={{ color: 'var(--icon-support)' }}>
                    •
                  </span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="panel-card chart-card">
          <div className="section-heading">
            <div>
              <h3 className="section-title" style={{ fontSize: '1.125rem', lineHeight: '1.75rem' }}>
                Mood trends
              </h3>
              <p className="muted-copy">
                A clear line showing how your emotional tone has shifted across all saved entries.
              </p>
            </div>
            <TrendingUp className="h-5 w-5" style={{ color: 'var(--icon-support)' }} />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trends} margin={{ top: 12, right: 14, bottom: 8, left: 10 }}>
              <defs>
                <linearGradient id="moodAreaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--chart-fill-start)" />
                  <stop offset="100%" stopColor="var(--chart-fill-end)" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="4 6" vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
              <YAxis
                domain={[0, 10]}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Score (0-10)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -2,
                  style: { fill: 'var(--text-secondary)', fontSize: 12 },
                }}
              />
              <Tooltip
                content={<ChartTooltip metricLabel="Mood" />}
                cursor={{ stroke: 'var(--chart-cursor)', strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="avg_sentiment"
                stroke="none"
                fill="url(#moodAreaFill)"
              />
              <Line
                type="monotone"
                dataKey="avg_sentiment"
                stroke="var(--chart-line)"
                strokeWidth={3.5}
                strokeLinecap="round"
                dot={{ fill: 'var(--chart-line)', strokeWidth: 0, r: 4.5 }}
                activeDot={{ r: 6, fill: 'var(--chart-line)' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="panel-card chart-card">
          <div className="section-heading">
            <div>
              <h3 className="section-title" style={{ fontSize: '1.125rem', lineHeight: '1.75rem' }}>
                Stress levels
              </h3>
              <p className="muted-copy">
                Notice when heavier days start clustering together over time.
              </p>
            </div>
            <Heart className="h-5 w-5" style={{ color: 'var(--icon-heart)' }} />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trends} margin={{ top: 12, right: 14, bottom: 8, left: 10 }}>
              <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="4 6" vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
              <YAxis
                domain={[0, 10]}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Score (0-10)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -2,
                  style: { fill: 'var(--text-secondary)', fontSize: 12 },
                }}
              />
              <Tooltip
                content={<ChartTooltip metricLabel="Stress" />}
                cursor={{ fill: 'var(--chart-bar-cursor)' }}
              />
              <Bar dataKey="avg_stress" fill="var(--chart-bar)" radius={[12, 12, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="disclaimer-card p-4">
        <div className="flex items-start gap-2">
          <AlertCircle
            className="h-5 w-5 mt-0.5 flex-shrink-0"
            style={{ color: 'var(--alert-text)' }}
          />
          <div className="text-sm disclaimer-copy">
            <strong>AI analysis disclaimer:</strong> These patterns and insights can be useful for
            reflection, but they are not a substitute for professional mental health advice.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
